import { DatePicker, Tooltip } from "antd";
import { UiButton, UiRangePicker, UiTable } from "../../../components";
import dayjs from "dayjs";
import { consultationAmountIcon, downloadReport, gstIcon, lawyerFees, penaltyIcon, refundIcon, revenueIcon } from "../../../assets/images";
import React, { useEffect, useRef, useState } from "react";
import { CameraFilled, CloseCircleFilled, RightOutlined, LeftOutlined, UserOutlined, PauseCircleOutlined, ClockCircleOutlined, TeamOutlined, ThunderboltOutlined, StopOutlined, CheckCircleOutlined, ExclamationCircleOutlined, SyncOutlined, AuditOutlined } from '@ant-design/icons';
import "./style.scss";
import Link from "antd/es/typography/Link";
import ConsultationChart from "./charts/consultationChart";
import * as api from "../../../actions";
import { CONSULTATION } from "../../../apiServices/endpoints";
import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { loaderReducer } from "../../../reducer/loader.reducer";
import { buildBarChartInput, complaintChartDetails, LAWYER_COLUMN_HEADER, overviewAmountChartDetails, supportChartDetails } from "./config";
import { useNavigate } from "react-router-dom";
import { formatUpToOneCrore, rupeeSymbol } from "../../../utils/appUtils";
import DoughnutChart from "./charts/DoughnutChart";
import { showErrorNotification } from "../../../components/uiNotification";
import BarChart from "./charts/BarChart";
import LineChart from "./charts/LineChart";
import { useReactToPrint } from "react-to-print";
import DashbordReport from "./report";

const PrintdashboardReport = React.forwardRef((props, ref) => {
    return (
        <div ref={ref}>
            <DashbordReport data={props?.data} fromDate={props?.fromDate} toDate={props?.toDate} year={props?.year} selectedMonth={props?.selectedMonth} />
        </div>
    );
});

const Dashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const reportRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => reportRef.current,
        onAfterPrint: () => setDashboardFilters((prev) => ({ ...prev, isReport: false, category: "LAWYER" })),
        pageStyle: `body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
                }
                 .dashboard-report::before {
                    content: "";
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    border: 2px solid #005D60;
                    z-index: 9999;
                    pointer-events: none;
                }
           canvas {
          max-width: 100% !important;
          height: auto !important;
        }`
    });

    const [dashboardFilters, setDashboardFilters] = useState({ category: "LAWYER", month: dayjs(), monthString: dayjs().format("YYYY-MM"), year: "", fromDate: "", toDate: "", activeTab: "ACTIVE", activeTabKey: "1", isReport: false });
    const [dashboardData, setDashboardData] = useState({});
    const [isShowOverView, setIsShowOverView] = useState(false);
    const [overviewData, setOverViewData] = useState({});

    const getAllDashboarData = (dashboardFilters) => {
        const queryParams = new URLSearchParams();
        if (dashboardFilters?.activeTab) {
            if (dashboardFilters?.activeTab === "REJECTED") {
                queryParams.set("approvalStatus", dashboardFilters?.activeTab);
            } else {
                queryParams.set("userStatus", dashboardFilters?.activeTab);
            }
        }
        if (dashboardFilters?.category) {
            queryParams.set("category", dashboardFilters?.category);
        }
        if (dashboardFilters?.fromDate && dashboardFilters?.toDate) {
            queryParams.set("startDate", dashboardFilters?.fromDate);
            queryParams.set("endDate", dashboardFilters?.toDate);
        } else if (dashboardFilters?.monthString) {
            queryParams.set("month", dashboardFilters?.monthString);
        } else if (dashboardFilters?.year) {
            const yearStr = dayjs(dashboardFilters?.year).isValid() ? dayjs(dashboardFilters?.year).format("YYYY") : "";
            if (yearStr) queryParams.set("year", yearStr);
        }
        if (dashboardFilters?.isReport) {
            queryParams.set("isReport", dashboardFilters?.isReport);
        }
        const queryString = queryParams.toString();
        return api.get(`${CONSULTATION}/admin_dashboard${queryString ? `?${queryString}` : ""}`);
    };

    const getAllOverviewData = (dashboardFilters) => {
        const queryParams = new URLSearchParams();
        if (dashboardFilters?.fromDate && dashboardFilters?.toDate) {
            queryParams.set("startDate", dashboardFilters?.fromDate);
            queryParams.set("endDate", dashboardFilters?.toDate);
        } else if (dashboardFilters?.monthString) {
            queryParams.set("month", dashboardFilters?.monthString);
        } else if (dashboardFilters?.year) {
            const yearStr = dayjs(dashboardFilters?.year).isValid() ? dayjs(dashboardFilters?.year).format("YYYY") : "";
            if (yearStr) queryParams.set("year", yearStr);
        }
        const queryString = queryParams.toString();
        return api.get(`${CONSULTATION}/dashboard_overview${queryString ? `?${queryString}` : ""}`);
    };

    const selectedYear = dashboardFilters?.year && dayjs(dashboardFilters?.year).isValid() ? dayjs(dashboardFilters?.year).format("YYYY") : "";

    const { isFetching: isFetchAllDashboadData } = useQuery(
        ["FETCH_ALL_DASHBOARDATA", dashboardFilters?.activeTab, dashboardFilters?.category, dashboardFilters?.fromDate, dashboardFilters?.toDate, dashboardFilters?.monthString, selectedYear, dashboardFilters?.isReport],
        () => getAllDashboarData(dashboardFilters),
        {
            onSuccess: (response) => {
                if (response?.statusCode === 200) {
                    const adminDashboard = response?.result?.adminDashboard ?? response?.result;
                    setDashboardData((prev) => ({
                        ...prev,
                        ...adminDashboard
                    }));
                }
            },
            enabled: Boolean(!isShowOverView && (dashboardFilters?.category || dashboardFilters?.fromDate || dashboardFilters?.toDate || dashboardFilters?.monthString || selectedYear || dashboardFilters?.activeTab || dashboardFilters?.isReport)),
            refetchOnWindowFocus: false
        }
    );

    const { isFetching: isFetchAllOverView } = useQuery(
        ["FETCH_ALL_OVERVIEW", dashboardFilters?.fromDate, dashboardFilters?.toDate, dashboardFilters?.monthString, selectedYear],
        () => getAllOverviewData(dashboardFilters),
        {
            onSuccess: (overviewResponse) => {
                if (overviewResponse?.statusCode === 200) {
                    const result = overviewResponse?.result;
                    const overviewRes = result?.dashboardOverviewResponse ?? result?.adminDashboard ?? result ?? {};
                    setOverViewData(overviewRes);
                    const adminDashboard = result?.adminDashboard ?? (result?.tdsAmount !== undefined || result?.todayTdsAmount !== undefined ? result : null);
                    if (adminDashboard) {
                        setDashboardData((prev) => ({
                            ...prev,
                            ...adminDashboard
                        }));
                    }
                } else {
                    showErrorNotification("Error", overviewResponse?.message);
                }
            },
            refetchOnWindowFocus: false
        }
    );

    const handleChange = (fieldValue, fieldName) => {
        setDashboardData({});
        setOverViewData({});
        if (fieldName === "date") {
            if (!fieldValue) {
                setDashboardFilters((prev) => ({
                    ...prev,
                    date: null,
                    fromDate: "",
                    toDate: ""
                }));
            } else {
                const fromDate = dayjs(fieldValue?.[0]).format("YYYY-MM-DD");
                const toDate = dayjs(fieldValue?.[1]).format("YYYY-MM-DD");
                setDashboardFilters((prev) => ({
                    ...prev,
                    date: fieldValue,
                    fromDate: fromDate || "",
                    toDate: toDate || "",
                    month: null,
                    monthString: "",
                    year: ""
                }));
            }
        } else if (fieldName === "month") {
            if (!fieldValue) {
                setDashboardFilters((prev) => ({
                    ...prev,
                    month: null,
                    monthString: "",
                    fromDate: "",
                    toDate: "",
                    year: ""
                }));
            } else {
                const monthValue = dayjs(fieldValue).format("YYYY-MM");
                setDashboardFilters((prev) => ({
                    ...prev,
                    month: fieldValue,
                    monthString: monthValue,
                    fromDate: "",
                    toDate: "",
                    year: ""
                }));
            }
        } else if (fieldName === "year") {
            setDashboardFilters((prev) => ({
                ...prev,
                year: fieldValue || "",
                monthString: "",
                month: null,
                fromDate: "",
                toDate: ""
            }));
        } else {
            setDashboardFilters((prev) => ({
                ...prev,
                [fieldName]: fieldValue,
                fromDate: "",
                toDate: "",
                month: null,
                monthString: "",
                year: ""
            }));
        }
    };

    const handleOpenOverView = () => {
        setIsShowOverView(true);
    };

    const handleCloseOverView = () => {
        setIsShowOverView(false);
    };

    const handleScreenNavigation = (screenName) => {
        if (screenName === "consultation") {
            navigate('/Consultations');
        }
        else if (screenName === "LAWYER") {
            navigate('/Lawyer');
        }
        else if (screenName === "CUSTOMER") {
            navigate('/Customer');
        }
        else if (screenName === "complaints") {
            navigate('/Complaints');
        }
        else if (screenName === "support") {
            navigate('/Support');
        }
    };

    const handleReport = () => {
        setDashboardFilters((prev) => ({ ...prev, isReport: true, category: "" }));
    };

    const { configData, data } = buildBarChartInput(overviewData?.caseCountList);
    const overviewAmounts = overviewData?.overviewAmount 
        ?? overviewData?.adminDashboard 
        ?? (overviewData?.totalConsultationAmount !== undefined ? overviewData : {});

    const resolvedTdsAmount = dashboardData?.tdsAmount 
        ?? dashboardData?.totalTdsAmount 
        ?? overviewAmounts?.tdsAmount 
        ?? overviewAmounts?.totalTdsAmount 
        ?? 0;

    const resolvedTodayTdsAmount = dashboardData?.todayTdsAmount 
        ?? dashboardData?.todayTds 
        ?? overviewAmounts?.todayTdsAmount 
        ?? overviewAmounts?.todayTds 
        ?? 0;

    const amountSummaryData = { 
        ...overviewAmounts,
        ...dashboardData, 
        tdsAmount: resolvedTdsAmount,
        totalTdsAmount: resolvedTdsAmount,
        todayTdsAmount: resolvedTodayTdsAmount
    };

    const parseCountValue = (...possibleValues) => {
        const validCount = possibleValues?.find((value) => Number?.isFinite(Number(value)));
        return validCount ? Number(validCount) : 0;
    };

    const adminStatsData = [
        { label: "Total Advocates", value: parseCountValue(dashboardData?.adminLawyerDetails?.lawyerCount, dashboardData?.totalLawyers, dashboardData?.totalAdvocates, dashboardData?.totalLawyer, dashboardData?.totalAdvocate), icon: <UserOutlined className="dashboard-stat-icon" /> },
        { label: "Active Advocates", value: parseCountValue(dashboardData?.adminLawyerDetails?.activeLawyers, dashboardData?.activeLawyers), icon: <UserOutlined className="dashboard-stat-icon" /> },
        { label: "Advocates On-hold", value: parseCountValue(dashboardData?.adminLawyerDetails?.lawyersOnHold, dashboardData?.holdLawyers), icon: <PauseCircleOutlined className="dashboard-stat-icon" /> },
        { label: "Not Approved Advocates", value: parseCountValue(dashboardData?.adminLawyerDetails?.pendingLawyers, dashboardData?.pendingLawyers), icon: <ExclamationCircleOutlined className="dashboard-stat-icon" /> },
        { label: "Partially Updated Advocates", value: parseCountValue(dashboardData?.adminLawyerDetails?.partialUpdateLawyers, dashboardData?.partialUpdateLawyers), icon: <SyncOutlined className="dashboard-stat-icon" /> },
        { label: "Penalty Pending (By Advocates)", value: parseCountValue(dashboardData?.adminLawyerDetails?.penaltyPending, dashboardData?.pendingLawyers, dashboardData?.pendingAdvocates, dashboardData?.totalPendingLawyers, dashboardData?.totalPendingAdvocates), icon: <ClockCircleOutlined className="dashboard-stat-icon" /> },
        { label: "Rejected Advocate", value: parseCountValue(dashboardData?.adminLawyerDetails?.rejectedLawyers, dashboardData?.rejectedLawyers), icon: <StopOutlined className="dashboard-stat-icon" /> },
        { label: "Instantly Available Advocates (Today)", value: parseCountValue(dashboardData?.adminLawyerDetails?.instantlyAvailableLawyers, dashboardData?.instantAvailableLawyers, dashboardData?.instantAvailableAdvocates, dashboardData?.instantAvailableAdvocate, dashboardData?.instantAvailableLawyer), icon: <ThunderboltOutlined className="dashboard-stat-icon" /> },
        { label: "Advocates Availability Count", value: parseCountValue(dashboardData?.adminLawyerDetails?.lawyerAvailabilityCount, dashboardData?.advocateAvailabilityCount, dashboardData?.lawyerAvailabilityCount, dashboardData?.availableLawyersCount, dashboardData?.availableLawyers, dashboardData?.lawyerAvailability), icon: <CheckCircleOutlined className="dashboard-stat-icon" /> },
        { label: "Total Clients", value: parseCountValue(dashboardData?.adminCustomerDetails?.customerCount, dashboardData?.totalCustomers, dashboardData?.totalCustomer), icon: <TeamOutlined className="dashboard-stat-icon" /> },
        { label: "Active Client", value: parseCountValue(dashboardData?.adminCustomerDetails?.activeCustomerCount, dashboardData?.activeCustomers), icon: <TeamOutlined className="dashboard-stat-icon" /> },
        { label: "Client On-hold", value: parseCountValue(dashboardData?.adminCustomerDetails?.holdCustomerCount, dashboardData?.holdCustomers, dashboardData?.holdCustomer, dashboardData?.totalHoldCustomers, dashboardData?.totalHoldCustomer), icon: <PauseCircleOutlined className="dashboard-stat-icon" /> },
    ];

    useEffect(() => {
        if (dashboardFilters?.isReport && !isFetchAllDashboadData) {
            handlePrint();
        }
    }, [dashboardFilters?.isReport, isFetchAllDashboadData, handlePrint]);

    useEffect(() => {
        const isLoading = isFetchAllDashboadData || isFetchAllOverView;
        dispatch(loaderReducer(isLoading));
    }, [dispatch, isFetchAllDashboadData, isFetchAllOverView]);

    return <>
        <div className="flexible">
            {
                isShowOverView ? <div>
                    <h6 style={{ padding: "10px", margin: 0 }} onClick={handleCloseOverView}> <LeftOutlined style={{ cursor: "pointer" }} /> Overview</h6>
                </div> : <div>
                    <h6 style={{ padding: "10px", margin: 0 }}>Dashboard</h6>
                </div>
            }
            <div className="flexible">
                <UiRangePicker name="date" value={dashboardFilters?.fromDate && dashboardFilters?.toDate ? [dayjs(dashboardFilters?.fromDate), dayjs(dashboardFilters?.toDate)] : null} disabledDate={(current) => current && current > dayjs().endOf("day")} onChange={(date) => handleChange(date, "date")} />
                <DatePicker picker="month" allowClear={false} value={dashboardFilters?.month || null} format="MMM" name="month" disabledDate={(current) => current && current > dayjs().endOf("day")} onChange={(month) => handleChange(month, "month")} />
                <DatePicker picker="year" value={dayjs(dashboardFilters?.year).isValid() ? dayjs(dashboardFilters?.year) : null} format="YYYY" name="year" disabledDate={(current) => current && current > dayjs().endOf("day")} onChange={(year) => handleChange(year, "year")} />
                {
                    !isShowOverView && <UiButton type="primary" onClick={handleReport}> <img src={downloadReport} alt="" />Download Report</UiButton>
                }
            </div>
        </div>
        <div style={{ padding: "10px 0px" }}>
            <div className="d-flex gap-2 w-100">
                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <img src={consultationAmountIcon} alt="" />
                        <p className="detail-title">Total Cons. Amt.</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalConsultationAmount > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalConsultationAmount?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalConsultationAmount)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today Cons. : <span>{amountSummaryData?.todayConsultationAmount?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <img src={revenueIcon} alt="" />
                        <p className="detail-title">Revenue</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalRevenue > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalRevenue?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalRevenue)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today Revenue : <span>{amountSummaryData?.todayRevenue?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <img src={refundIcon} alt="" />
                        <p className="detail-title">Refund Amt.</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalRefundAmount > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalRefundAmount?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalRefundAmount)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today Refund : <span>{amountSummaryData?.todayRefundAmount?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <img src={lawyerFees} alt="" />
                        <p className="detail-title">Advocate Fees</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalLawyerFee > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalLawyerFee?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalLawyerFee)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today Advocate Fees : <span>{amountSummaryData?.todayLawyerFee?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <img src={penaltyIcon} alt="" />
                        <p className="detail-title">Penalty Amt.</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalPenaltyAmount > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalPenaltyAmount?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalPenaltyAmount)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today Penalty : <span>{amountSummaryData?.todayPenaltyAmount?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <CloseCircleFilled style={{ fontSize: 18, color: "#767676" }} />
                        <p className="detail-title">Auto-Cancl.Amt.</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalAutocancelAmount > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalAutocancelAmount?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalAutocancelAmount)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today Amt. : <span>{amountSummaryData?.todayAutocancelAmount?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <CameraFilled style={{ fontSize: 18, color: "#767676" }} />
                        <p className="detail-title">Recording Session Amt.</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalRecordAmount > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalRecordAmount?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalRecordAmount)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today Amt. : <span>{amountSummaryData?.todayRecordAmount?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <img src={gstIcon} alt="" />
                        <p className="detail-title">GST Amt.</p>
                    </div>
                    <div>
                        <Tooltip
                            title={amountSummaryData?.totalConsultationGstAmount > 10000000 ? <>{rupeeSymbol} {amountSummaryData?.totalConsultationGstAmount?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.totalConsultationGstAmount)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today GST : <span>{amountSummaryData?.todayConsultationGstAmount?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                <div style={{ width: isShowOverView ? "16%" : "14%" }}>
                    <div className="d-flex justify-content-left gap-2">
                        <AuditOutlined style={{ fontSize: 18, color: "#767676" }} />
                        <p className="detail-title">TDS Amt.</p>
                    </div>
                    <div>
                        <Tooltip
                            title={Number(amountSummaryData?.tdsAmount ?? amountSummaryData?.totalTdsAmount) > 10000000 ? <>{rupeeSymbol} {Number(amountSummaryData?.tdsAmount ?? amountSummaryData?.totalTdsAmount)?.toLocaleString("en-IN")}</> : ""}
                            placement="top"
                        >
                            <h2 className="detail-Amounts">
                                {rupeeSymbol} {formatUpToOneCrore(amountSummaryData?.tdsAmount ?? amountSummaryData?.totalTdsAmount)}
                            </h2>
                        </Tooltip>
                        <p className="detail-title">Today TDS : <span>{Number(amountSummaryData?.todayTdsAmount ?? 0)?.toLocaleString("en-IN")}</span></p>
                    </div>
                </div>
                {
                    !isShowOverView && <div style={{ width: "14%" }}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80px" }}>
                            <Link style={{ fontWeight: 700 }} onClick={handleOpenOverView}>See More <RightOutlined /></Link>
                        </div>
                    </div>
                }
            </div>
        </div>
        {
            !isShowOverView && <div style={{ padding: "0px 0px 10px 0px" }}>
                <div className="flexible">
                    <div className="chart-container-col1">
                        <ConsultationChart handleScreenNavigation={handleScreenNavigation} dashboardData={dashboardData} />
                    </div>
                    <div className="chart-container-col2" style={{ display: "flex", flexDirection: "column" }}>
                        <div className="flexible">
                            <h6 style={{ fontWeight: 700 }}>Complaint</h6>
                            <Link style={{ fontWeight: 700 }} onClick={() => handleScreenNavigation("complaints")}>See More <RightOutlined /></Link>
                        </div>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                            <div style={{ width: "400px" }}>
                                <DoughnutChart height={270} configData={complaintChartDetails} data={complaintChartDetails?.map((detail) => dashboardData?.[detail?.key])} />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flexible">
                    <div style={{ width: "70%" }}>
                        <div className="flexible">
                            <div>
                            </div>
                        </div>
                        <div className="dashboard-stats-grid">
                            {
                                adminStatsData?.map((statItem) => (
                                    <div className="dashboard-stat-card" key={statItem?.label}>
                                        <div className="dashboard-stat-header">
                                            {statItem?.icon}
                                            <p className="dashboard-stat-label">{statItem?.label}</p>
                                        </div>
                                        <h4 className="dashboard-stat-value">{statItem?.value?.toLocaleString("en-IN")}</h4>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="chart-container-col2" style={{ display: "flex", flexDirection: "column" }}>
                        <div className="flexible">
                            <h6 style={{ fontWeight: 700 }}>Support</h6>
                            <Link style={{ fontWeight: 700 }} onClick={() => handleScreenNavigation("support")}>See More <RightOutlined /></Link>
                        </div>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                            <div style={{ width: "400px" }}>
                                <DoughnutChart height={270} configData={supportChartDetails} data={supportChartDetails?.map((detail) => dashboardData?.[detail?.key])} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        }
        {
            isShowOverView && <div style={{ height: "80vh" }}>
                <div className="flexible" >
                    <div className="overview-chart-col1">
                        <h6 style={{ fontWeight: 700 }}>Revenue</h6>
                        <div style={{ height: 300, paddingTop: "40px" }}>
                            <LineChart lineData={overviewData?.revenueList} color={"#005D60"} />
                        </div>
                    </div>
                    <div className="overview-chart-col2">
                        <h6>Overall Amount Overview</h6>
                        <div style={{ height: 300, paddingTop: "40px" }}>
                            <BarChart configData={overviewAmountChartDetails} data={overviewAmountChartDetails?.map((detail) => overviewData?.overviewAmount?.[detail?.key])} horizontal={false} />
                        </div>
                    </div>
                </div>
                <div className="flexible" >
                    <div className="overview-chart-col2">
                        <h6>Case Revenue</h6>
                        <div style={{ height: 300, paddingTop: "40px" }}>
                            <BarChart configData={configData} data={data} horizontal={false} />
                        </div>
                    </div>
                    <div className="overview-chart-col1">
                        <h6>Top 5 Earning in Advocate</h6>
                        <div style={{ height: 300 }}>
                            <UiTable
                                columns={LAWYER_COLUMN_HEADER()}
                                dataSource={overviewData?.topEarningLawyers}
                                pagination={false}
                                scroll={{ x: "max-content" }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        }
        <div style={{ position: "absolute", top: "-10000000px" }}>
            <PrintdashboardReport ref={reportRef} data={amountSummaryData} fromDate={dashboardFilters?.fromDate} toDate={dashboardFilters?.toDate} year={dashboardFilters?.year} selectedMonth={dashboardFilters?.monthString} />
        </div>
    </>
};
export default Dashboard